import { useCallback, useMemo, useState } from 'react'
import { useRedtailClientLink, useLinkRedtailClient } from '../../../api/redtail'
import { normalizeLinkConflict } from './helpers'

// `mutationError` and `linkQueryError` are kept separate so the dialog only
// renders mutation failures and the gate can pass through on a failed read.
export function useRedtailClientLinkState (clientId) {
  const linkQuery = useRedtailClientLink(clientId)
  const linkMutation = useLinkRedtailClient()
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [conflict, setConflict] = useState(null)

  const openDialog = useCallback(() => setDialogOpen(true), [])
  // Don't carry the previous attempt's error banner across opens / steps.
  const resetMutationError = useCallback(() => linkMutation.reset(), [linkMutation])
  const closeDialog = useCallback(() => {
    setDialogOpen(false)
    setConflict(null)
    resetMutationError()
  }, [resetMutationError])
  const resetConflict = useCallback(() => setConflict(null), [])

  const link = useCallback(async ({ primaryContactId, familyName, acknowledgeConflict } = {}) => {
    try {
      const result = await linkMutation.mutateAsync({
        clientId,
        primaryContactId,
        familyName,
        acknowledgeConflict
      })
      setConflict(null)
      return result
    } catch (err) {
      const normalized = normalizeLinkConflict(err)
      if (normalized) setConflict(normalized)
      throw err
    }
  }, [clientId, linkMutation])

  const isLoading = Boolean(linkQuery.isLoading)

  return useMemo(() => ({
    isLoading,
    linked: linkQuery.data?.linked ?? null,
    externalId: linkQuery.data?.externalId ?? null,
    isDialogOpen,
    openDialog,
    closeDialog,
    link,
    isLinking: linkMutation.isLoading,
    conflict,
    resetConflict,
    mutationError: linkMutation.error || null,
    resetMutationError,
    linkQueryError: linkQuery.error || null,
    clientId
  }), [
    isLoading,
    linkQuery.data?.linked,
    linkQuery.data?.externalId,
    linkQuery.error,
    isDialogOpen,
    openDialog,
    closeDialog,
    link,
    linkMutation.isLoading,
    linkMutation.error,
    conflict,
    resetConflict,
    resetMutationError,
    clientId
  ])
}
